import {
  Container,
  Box,
  Typography,
  styled,
  Paper,
  Stack,
} from "@mui/material";
import useMapImpactSlider from "hooks/maps/useMapImpactSlider";
import { selectReferenceExperience } from "redux/slices/shed/shedSlice";
import useMapShareTripParams from "hooks/maps/useMapShareTripParams";
import { useAppSelector } from "redux/hooks";
import AnimatedCounter from "components/core/AnimatedNumber";
import GiftHelpIcon from "components/icons/GiftHelpIcon.svg?react";
import HandHelpIcon from "components/icons/HandHelpIcon.svg?react";
import PhoneHelpIcon from "components/icons/PhoneHelpIcon.svg?react";

const StatCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "color" && prop !== "borderColor",
})<{ color?: string; borderColor?: string }>(
  ({ theme, color, borderColor }) => ({
    borderRadius: 16,
    padding: theme.spacing(2),
    textAlign: "center",
    flex: 1,
    height: "100%",
    backgroundColor: color || theme.palette.background.paper,
    border: borderColor ? `2px solid ${borderColor}` : "none",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    justifyContent: "center",
    alignItems: "center",
  })
);

const MapEmbedCalculatorFeedbackPage = () => {
  useMapShareTripParams();

  const mapExperience = useAppSelector(selectReferenceExperience);
  const { savedTrees, savedAreaKilometers, savedAreaSoccerFields, savedCO2 } =
    useMapImpactSlider({
      mapExperience,
    });

  return (
    <Container maxWidth="md" sx={{ my: 3 }}>
      <Stack spacing={4}>
        {/* Top Row */}
        <Box
          display="flex"
          justifyContent="space-between"
          gap={2}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <StatCard color="#DDECC0" elevation={0}>
            <GiftHelpIcon width={48} height={48} />
            <Typography variant="h5" sx={{ fontWeight: 400, color: "#00373e" }}>
              100 000 kr
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Årlig fast donasjon
            </Typography>
          </StatCard>
          <StatCard color="#87A9AE" elevation={0}>
            <PhoneHelpIcon width={48} height={48} />
            <Typography variant="h5" sx={{ fontWeight: 400, color: "#00373e" }}>
              50 000 kr
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Fra kundetilbakemeldinger
            </Typography>
          </StatCard>
          <StatCard color="#DDECC0" borderColor="#a2ce57" elevation={0}>
            <HandHelpIcon width={48} height={48} />
            <Typography variant="h5" sx={{ fontWeight: 400, color: "#00373e" }}>
              <AnimatedCounter value={savedTrees} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Trær reddet totalt
            </Typography>
          </StatCard>
        </Box>

        {/* Progress Bar */}
        <Stack gap={0.5}>
          {/* Title */}
          <Typography textAlign="center" sx={{ color: "#00373e" }}>
            Hvordan vi bidrar
          </Typography>
          <Stack
            direction="row"
            sx={{ height: 48, borderRadius: 1, overflow: "hidden" }}
          >
            <Box
              sx={{
                width: "67%",
                backgroundColor: "#DDECC0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: "#00373e" }}>
                Fast donasjon: 67%
              </Typography>
            </Box>
            <Box
              sx={{
                width: "33%",
                backgroundColor: "#87A9AE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#00373e", wordBreak: "break-all" }}
              >
                Kundetilbakemeldinger: 33%
              </Typography>
            </Box>
          </Stack>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              0 kr
            </Typography>
            <Typography variant="caption" color="text.secondary">
              150 000 kr totalt
            </Typography>
          </Box>
        </Stack>

        {/* Footer */}
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          gap={2}
        >
          <Box
            sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 12px)" } }}
          >
            <StatCard color="#DDECC0" elevation={0}>
              <Typography
                variant="h5"
                sx={{ color: "#a2ce57", fontWeight: 400 }}
              >
                <AnimatedCounter value={savedAreaKilometers} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                km² reddet
              </Typography>
            </StatCard>
          </Box>
          <Box
            sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 12px)" } }}
          >
            <StatCard color="#DDECC0" elevation={0}>
              <Typography
                variant="h5"
                sx={{ color: "#a2ce57", fontWeight: 400 }}
              >
                <AnimatedCounter value={Math.trunc(savedAreaSoccerFields)} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fotballbaner
              </Typography>
            </StatCard>
          </Box>
          <Box
            sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 12px)" } }}
          >
            <StatCard color="#DDECC0" elevation={0}>
              <Typography
                variant="h5"
                sx={{ color: "#a2ce57", fontWeight: 400 }}
              >
                <AnimatedCounter value={savedCO2} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tonn CO₂ redusert
              </Typography>
            </StatCard>
          </Box>
          <Box
            sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 12px)" } }}
          >
            <StatCard color="#DDECC0" elevation={0}>
              <Typography
                variant="h5"
                sx={{ color: "#a2ce57", fontWeight: 400 }}
              >
                <AnimatedCounter value={savedTrees} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Trær reddet
              </Typography>
            </StatCard>
          </Box>
        </Box>
      </Stack>
    </Container>
  );
};

export default MapEmbedCalculatorFeedbackPage;
